import React, { useState, useEffect, useCallback } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useNavigate } from "react-router-dom";
import { getter } from "@progress/kendo-react-common";
import { useDispatch } from "react-redux";
import CustomCellClick from "../../Grid/CustomCellClick";

import { loadClientTicketGridData } from "../../../handlers/ClientDetail/ClientTicketHandler";

import useReloadButton from "../../../CustomHooks/useReloadButton";
import { useKendoGridHandlers } from "../../../CustomHooks/useKendoGridHandlers";
import { setSearchFrom } from "../../../store/reducers/SharedLayoutDataSlice";

const DATA_ITEM_KEY = "SEQ_NUM";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO_CLIENT_TICKET = {
	action: "SEARCHTICKETS",
	className: "DASHBOARD",
	pageSize: 20,
	currentPageNumber: 1,
	sortName: "ENTITY_DESCRIPTION",
	sortOrder: "desc",
	gridHeight: "300px",
};

const PAGER_SETTINGS = {
	info: true,
	type: "input",
	previousNext: true,
	pageSize: 4,
	refresh: true,
};

const KendoClientDetailTickets = (props) => {
	const dispatch = useDispatch();
	const { status, clientId, type } = props;
	const [gridData, setGridData] = useState([]);
	const [total, setTotal] = useState(0);
	const [reloadToggle, setReloadToggle] = useState(false);

	const navigate = useNavigate();

	const [gridState, setGridState] = useState({
		pageNumber: DEFAULT_GRID_INFO_CLIENT_TICKET.currentPageNumber,
		pageSize: DEFAULT_GRID_INFO_CLIENT_TICKET.pageSize,
		sortName: DEFAULT_GRID_INFO_CLIENT_TICKET.sortName,
		sortOrder: DEFAULT_GRID_INFO_CLIENT_TICKET.sortOrder,
		sort: [
			{
				field: DEFAULT_GRID_INFO_CLIENT_TICKET.sortName,
				dir: DEFAULT_GRID_INFO_CLIENT_TICKET.sortOrder?.toLowerCase() || "asc",
			},
		]
	});

	const { HandleDataStateChange, HandlePageChange } =
		useKendoGridHandlers(setGridState);

	const [selectedState, setSelectedState] = React.useState();

	// UseEffect to load the grid data
	useEffect(() => {
		loadClientTicketGridData(status, clientId, gridState).then((response) => {
			setGridData(response.rows);
			setTotal(response.total);
		});
	}, [status, gridState, reloadToggle]);

	useReloadButton(setReloadToggle);

	const CustomCellClicked = useCallback(
		(cellProps) => (
			<CustomCellClick
				{...cellProps}
				selectionChange={onSelectionChange}
				{...cellProps.additionalProps}
			/>
		),
		[]
	);

	const ShowTaskDetail = (taskId, ticketType) => {
		dispatch(setSearchFrom("clientDetail"))
		navigate("/ticketDetail", {
			state: {
				ticketNo: taskId,
				task: ticketType === "Training And Implementation",
			},
		});
	};

	const SelectedContentLength = () => {
		const selectedText = window.getSelection().toString();
		return selectedText.length > 0;
	};

	const onSelectionChange = React.useCallback(
		(event) => {
			const check = SelectedContentLength();
			if (!check) {
				const newSelectedState = getSelectedState({
					event,
					selectedState,
					dataItemKey: DATA_ITEM_KEY,
				});
				setSelectedState(newSelectedState);
				const currentColumn = event.target._columns[event.endColIndex].field;
				if (currentColumn === "SEQ_NUM") {
					ShowTaskDetail(event.dataItems[event.endRowIndex][currentColumn], event.dataItems[event.endRowIndex].TICKET_TYPE);
				}
			}
		},
		[selectedState]
	);

	const CellTicketStatus = (props) => {
		if (
			props.dataItem.STATUS === "O" ||
			props.dataItem.STATUS === "o" ||
			props.dataItem.STATUS === "Open"
		) {
			props.dataItem.STATUS = "Open";
			return (
				<td>
					<div className="flex">
						<div className="ticket-open w-100 m-0">{props.dataItem.STATUS}</div>
					</div>
				</td>
			);
		} if (
			props.dataItem.STATUS === "K" ||
			props.dataItem.STATUS === "k" ||
			props.dataItem.STATUS === "Reopened"
		) {
			props.dataItem.STATUS = "Reopened";
			return (
				<td>
					<div className="flex">
						<div className="ticket-open w-100 m-0">{props.dataItem.STATUS}</div>
					</div>
				</td>
			);
		} if (
			props.dataItem.STATUS === "I" ||
			props.dataItem.STATUS === "i" ||
			props.dataItem.STATUS === "In Progress"
		) {
			props.dataItem.STATUS = "In Progress";
			return (
				<td>
					<div className="flex">
						<div className="ticket-inProgrees ticket-status w-100 m-0">
							{props.dataItem.STATUS}
						</div>
					</div>
				</td>
			);
		} if (
			props.dataItem.STATUS === "Q" ||
			props.dataItem.STATUS === "q" ||
			props.dataItem.STATUS === "Pending On Client"
		) {
			props.dataItem.STATUS = "Pending On Client";
			return (
				<td>
					<div className="flex">
						<div className="ticket-pendingOnClient ticket-status w-100 m-0">
							{props.dataItem.STATUS}
						</div>
					</div>
				</td>
			);
		} if (
			props.dataItem.STATUS === "R" ||
			props.dataItem.STATUS === "r" ||
			props.dataItem.STATUS === "Resolved"
		) {
			props.dataItem.STATUS = "Resolved";
			return (
				<td>
					<div className="flex">
						<div className="ticket-resolved ticket-status w-100 m-0">
							{props.dataItem.STATUS}
						</div>
					</div>
				</td>
			);
		}
	};

	const CellComments = (props) => (
			<td className="Comments30 pl-4">
				<span className="textWrap" title={props.dataItem[props.field]}>
					{props.dataItem[props.field]}
				</span>
			</td>
		);

	const CellPracticeName = (props) => {
		if (props.dataItem.ENTITY_DESCRIPTION) {
			if (
				props.dataItem.ACTIVE_FLAG &&
				props.dataItem.ACTIVE_FLAG.toUpperCase() !== "Y"
			) {
				return (
					<td
						className="Comments30 pl-4"
						title={props.dataItem.ENTITY_DESCRIPTION}
						style={{ color: "red" }}
					>
						{props.dataItem.ENTITY_DESCRIPTION}
					</td>
				);
			} if (
				props.dataItem.ACCOUNT_TYPE &&
				props.dataItem.ACCOUNT_TYPE.toUpperCase() === "LIVE"
			) {
				return (
					<td
						className="Comments30 pl-4"
						title={props.dataItem.ENTITY_DESCRIPTION}
						style={{ color: "green" }}
					>
						{props.dataItem.ENTITY_DESCRIPTION}
					</td>
				);
			} 
				return (
					<td
						className="Comments30 pl-4"
						title={props.dataItem.ENTITY_DESCRIPTION}
						style={{ color: "orange" }}
					>
						{props.dataItem.ENTITY_DESCRIPTION}
					</td>
				);
			
		}
	};

	const MakeColumnTitle = () => {
		let columnName = "Task";
		if (type === "A") {
			columnName = "Ticket/Task";
		} else if (type === "Task") {
			columnName = "Task";
		} else {
			columnName = "Ticket";
		}
		return columnName;
	};


	return (
		<div>
			<Tooltip openDelay={100} position="right" anchorElement="target">
				<Grid
					style={{ height: "calc(100vh - 217px)" }}
					scrollable="scrollable"
					total={total}
					skip={(gridState.pageNumber - 1) * gridState.pageSize}
					pageSize={gridState.pageSize}
					filterable={false}
					sortable={{
						mode: "single",
						allowUnsort: false,
					}}
					sort={gridState.sort}
					selectedField={SELECTED_FIELD}
					data={gridData.map((item) => ({
						...item,
						[SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
					}))}
					selectable={{
						enabled: true,
						drag: false,
						cell: false,
						mode: "multiple",
					}}
					pageable={PAGER_SETTINGS}
					onPageChange={HandlePageChange}
					onDataStateChange={HandleDataStateChange}
					onSelectionChange={onSelectionChange}
				>
					<GridColumn
						title="Status"
						field="STATUS"
						sortable
						cell={CellTicketStatus}
						width={150}
					/>
					<GridColumn
						title={MakeColumnTitle()}
						field="SEQ_NUM"
						cell={CustomCellClicked}
						sortable
						width={130}
					/>
					<GridColumn
						title="Customer Id"
						field="ENTITY_SEQ_NUM"
						sortable
						width={140}
					/>
					<GridColumn
						title="Description"
						field="COMMENTS"
						sortable
						cell={CellComments}
						filterable={false}
						width={370}
					/>
					<GridColumn
						title="Practice Name"
						field="ENTITY_DESCRIPTION"
						cell={CellPracticeName}
						sortable
						width={150}
					/>
					<GridColumn
						title="Assignee"
						field="A_SHORT_NAME"
						className="pl-4"
						width={170}
					/>
					<GridColumn title="Reporter" field="REPORTER_SHORT_NAME" />
					<GridColumn
						title="Team"
						field="TEAMNAME"
						filterable={false}
						sortable
						width={150}
						className="pl-4"
					/>
					<GridColumn
						title="Priority"
						field="PRIORITY"
						filterable={false}
						sortable
						className="pl-4"
						width={150}
					/>
				</Grid>
			</Tooltip>
		</div >
	);
};
export default KendoClientDetailTickets;